import {StepperSelectionEvent} from '@angular/cdk/stepper';
import {formatCurrency} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  Validators
} from '@angular/forms';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {MatLegacyTableDataSource as MatTableDataSource} from '@angular/material/legacy-table';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {
  CustomFirestoreService,
  getIsLoggedIn,
  ItemizedCost,
  marketingWizardCreateAccount,
  marketingWizardDownlaod,
  marketingWizardEarlyAdopter,
  marketingWizardSelectPlan,
  marketingWizardWide,
  Price,
  selectAccountState,
  selectDeviceIsSmallScreen,
  selectHasActiveSubscription,
  selectProductsWithPricesCurrentSubscription,
  selectStorageProductsWithPriceCurrentSubscription,
  selectStripeCoupon,
  StripePaymentSelectionService,
  StripeProductWithPricesCurrentSubscription
} from '@spout/global-web/data-access';
import {
  AccountState,
  ENVIRONMENT,
  IEnvironmentState
} from '@spout/global-web/models';
import {hasValue} from '@uiux/fn';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  take,
  takeUntil
} from 'rxjs/operators';
import {EarlyAdopterModalComponent} from '../common/early-adopter-modal/early-adopter-modal.component';

@Component({
  selector: 'spt-payment-wizard-small',
  templateUrl: './payment-wizard-small.component.html',
  styleUrls: ['./payment-wizard-small.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentWizardSmallComponent implements OnInit {
  private _onDestroy$: Subject<boolean> = new Subject();

  selectedIndex$: BehaviorSubject<number>;
  priceComplete$: BehaviorSubject<boolean>;
  hasSubscription$: Observable<boolean>;
  createAccountComplete$: BehaviorSubject<boolean>;

  productsWithPricesCurrentSubscription$: Observable<
    StripeProductWithPricesCurrentSubscription[]
  >;
  storageProductsWithPriceCurrentSubscription$: Observable<
    StripeProductWithPricesCurrentSubscription[]
  >;

  planPriceIDForm = new UntypedFormControl(null, Validators.required);

  earlyAdopterAgreementForm = new UntypedFormControl(false);

  displayedColumns: string[] = ['itemName', 'itemPrice'];
  subscriptionItemized = new MatTableDataSource<ItemizedCost>([]);

  deviceIsMobile$ = this._store.pipe(select(selectDeviceIsSmallScreen));
  stripeCoupon$ = this._store.pipe(select(selectStripeCoupon));

  constructor(
    private _firestore: CustomFirestoreService,
    public payment: StripePaymentSelectionService,
    private fb: UntypedFormBuilder,
    private _store: Store,
    private _cd: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    @Inject(ENVIRONMENT) private environment: IEnvironmentState
  ) {
    this.selectedIndex$ = new BehaviorSubject<number>(0);
    this.priceComplete$ = new BehaviorSubject<boolean>(true);
    this.createAccountComplete$ = new BehaviorSubject<boolean>(false);
    this.hasSubscription$ = this._store.pipe(
      select(selectHasActiveSubscription)
    );
    this.productsWithPricesCurrentSubscription$ = this._store.pipe(
      select(selectProductsWithPricesCurrentSubscription)
    );
    this.storageProductsWithPriceCurrentSubscription$ = this._store.pipe(
      select(selectStorageProductsWithPriceCurrentSubscription)
    );
  }

  ngOnInit(): void {
    const that = this;

    // this.payment.init();

    this._store.dispatch(marketingWizardWide());

    // this.stripeFirestore.init();

    this.activatedRoute.queryParams
      .pipe(
        take(1),
        mergeMap((params: Params) => {
          return this._store.pipe(
            select(selectAccountState),
            map((account: AccountState) => {
              return [params, account];
            })
          );
        })
      )
      .subscribe(([params, account]) => {
        if (params && params['s'] !== undefined) {
          const step = parseInt(params['s'], 10);

          if (step === 1) {
            this._store.dispatch(marketingWizardCreateAccount());
          }
          if (step === 2) {
            this._store.dispatch(marketingWizardSelectPlan());
          }
          if (step === 3) {
            this._store.dispatch(marketingWizardDownlaod());
          }

          if (step > 1) {
            if (account.isLoaded) {
              if (hasValue(account.account.uid)) {
                this.selectedIndex$.next(step);
              } else {
                this.selectedIndex$.next(1);
              }
            }
          } else {
            this.selectedIndex$.next(step);
          }
        }
      });

    this._store
      .pipe(
        select(getIsLoggedIn),
        distinctUntilChanged(),
        filter((isLoggedIn: boolean) => isLoggedIn),
        takeUntil(this._onDestroy$)
      )
      .subscribe((isLoggedIn: boolean) => {
        if (isLoggedIn) {
          this.createAccountComplete$.next(isLoggedIn);
        }
        this._cd.detectChanges();
      });

    this.planPriceIDForm.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((priceID: string) => {
        if (priceID && priceID.length>2) {
          this.payment.updatePrice(priceID);
          this._cd.detectChanges();
        }
      });

    this.payment.itemizedCheckoutList$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((d: ItemizedCost[]) => {
        this.subscriptionItemized.data = [...d];
      });

    this.payment.selectedPlanPrice$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((d: string | null) => {
        this.planPriceIDForm.setValue(d);
      });
  }

  formatCurrency(p: Price): string {
    if (p && p.currency) {
      return formatCurrency(
        p.unit_amount / 100,
        'en-US',
        '$',
        p.currency.toUpperCase(),
        '1.2-2'
      );
    }

    return '';
  }

  selectionChange(v: StepperSelectionEvent) {
    this.router
      .navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {s: v.selectedIndex},
        queryParamsHandling: 'merge'
      })
      .then(() => {
        /* noop */
      });
  }

  learnMoreEarlyAdopter() {
    this._store.dispatch(marketingWizardEarlyAdopter());

    this.dialog.open(EarlyAdopterModalComponent, {
      backdropClass: 'studio-dialog-backdrop',
      width: '600px'
    });
  }

  ngOnDestroy() {
    this._onDestroy$.next(true);
  }
}
